/**
 * Binds workspace application state without entity identity to the runtime lifecycle (ADR 051): the
 * definition owns its endpoint and validation, and the result is held per runtime rather than cached
 * as a row in the entity store.
 *
 * `load` runs once per runtime during preparation and again on `refresh()`; its result lives and dies
 * with that runtime, and `use()` reads the active runtime's result without loading. `onLoaded` applies
 * each loaded result to the runtime it loaded for, such as registering collections on its store. A refresh
 * that settles after its runtime was displaced is discarded: neither `onLoaded` nor the value update runs,
 * so a retired runtime is never changed.
 */
import type { Ref, ShallowRef } from 'vue';
import type { WorkspaceRuntime, WorkspaceRuntimeStore } from '#layers/runtime/workspace/types';
import type { WorkspaceEntityStore } from './registry';
import type { HydrationContext, WorkspaceHydration, WorkspaceHydrationReader } from './workspace-hydration';
import { computed, ref, shallowRef } from 'vue';
import { workspaceRuntime } from '#layers/runtime/workspace/runtime';

type AnyRuntime = WorkspaceRuntime<WorkspaceRuntimeStore>;
type Runtime = WorkspaceRuntime<WorkspaceEntityStore>;

export function defineWorkspaceStateHydration<TValue, TEmpty>(
	load: (runtime: Runtime, context: HydrationContext) => Promise<TValue>,
	{ empty, onLoaded }: {
		empty: TEmpty;
		onLoaded?: (runtime: Runtime, value: TValue) => void;
	},
): WorkspaceHydration<TValue | TEmpty> {
	interface RuntimeLoad {
		context: HydrationContext;
		initial: Promise<void>;
		refreshing: Ref<boolean>;
		value: ShallowRef<TValue | TEmpty>;
	}

	const loads = new WeakMap<AnyRuntime, RuntimeLoad>();

	function activeLoad() {
		const runtime = workspaceRuntime.activeRuntime.value;
		return runtime ? loads.get(runtime) : undefined;
	}

	async function hydrate(anyRuntime: AnyRuntime, context: HydrationContext): Promise<TValue | TEmpty> {
		let runtimeLoad = loads.get(anyRuntime);

		if (!runtimeLoad) {
			if (!anyRuntime.scope.active) throw new Error(`Workspace runtime "${anyRuntime.slug}" is disposed`);

			// `workspaceRuntime` only prepares and activates runtimes it created over `WorkspaceEntityStore`.
			const runtime = anyRuntime as Runtime;
			const value = shallowRef<TValue | TEmpty>(empty);

			const initial = load(runtime, context).then((loaded) => {
				onLoaded?.(runtime, loaded);
				value.value = loaded;
			});

			runtimeLoad = { context, initial, refreshing: ref(false), value };
			loads.set(anyRuntime, runtimeLoad);
		}

		await runtimeLoad.initial;
		return runtimeLoad.value.value;
	}

	async function refresh() {
		const runtime = workspaceRuntime.activeRuntime.value;
		const runtimeLoad = runtime && loads.get(runtime);
		if (!runtime || !runtimeLoad) return;

		runtimeLoad.refreshing.value = true;

		try {
			const loaded = await load(runtime, runtimeLoad.context);
			if (workspaceRuntime.activeRuntime.value !== runtime) return;
			onLoaded?.(runtime, loaded);
			runtimeLoad.value.value = loaded;
		}
		finally {
			runtimeLoad.refreshing.value = false;
		}
	}

	function use(): WorkspaceHydrationReader<TValue | TEmpty> {
		const data = computed(() => {
			const runtimeLoad = activeLoad();
			return runtimeLoad ? runtimeLoad.value.value : empty;
		});
		const isRefreshing = computed(() => activeLoad()?.refreshing.value ?? false);

		return { data, isRefreshing, refresh };
	}

	return { hydrate, use };
}
